import { isConnectLandingEnabled } from 'pages/Welcome/components/ConnectLanding/parseConnectLandingConfig';
import { isKrasavaLandingEnabled } from 'pages/Welcome/components/KrasavaLanding/parseKrasavaLandingConfig';
import { resolveCommunityPalette, standardPalette } from './themePalette';

const CUSTOM_MARKETING_DOMAINS = ['cpocto', 'cpoctoavito'];

const MANAGED_PROPERTIES = [
    '--theme-primary',
    '--theme-background',
    '--theme-text',
    '--theme-muted',
    '--theme-header',
    '--theme-button',
    '--theme-button-text',
    '--theme-link',
    '--theme-border',
    '--theme-surface',
    '--background',
    '--foreground',
    '--card',
    '--card-foreground',
    '--popover',
    '--popover-foreground',
    '--primary-foreground',
    '--secondary',
    '--secondary-foreground',
    '--muted',
    '--muted-foreground',
    '--accent',
    '--accent-foreground',
    '--border',
    '--input',
    '--ring',
    '--primary-color',
    '--primary-background-color',
    '--accent-text-color',
    '--primary-text-color',
    '--primary-layer-color',
    '--navbar-bg',
    '--secondary-color',
    '--secondary-background-color',
    '--secondary-text-color',
    '--input-black-color',
    '--input-grey-color',
    '--light-border-color',
];

export function shouldSkipSharedPalette(pathname, community) {
    const path = String(pathname || '').split('?')[0].replace(/\/+$/, '') || '/';
    if (path !== '/') {
        return false;
    }
    if (isConnectLandingEnabled(community) || isKrasavaLandingEnabled(community)) {
        return true;
    }
    return CUSTOM_MARKETING_DOMAINS.includes(community?.domain);
}

function brandProperties(palette) {
    const button = palette.buttonBg || palette.primary;
    const link = palette.link || palette.primary;
    const header = palette.headerBg || '#ffffff';
    const buttonText = palette.buttonText || '#ffffff';
    return {
        '--theme-primary': palette.primary,
        '--theme-header': header,
        '--theme-button': button,
        '--theme-button-text': buttonText,
        '--theme-link': link,
        '--primary-color': palette.primary,
        '--primary-background-color': button,
        '--accent-text-color': link,
        '--navbar-bg': header,
        '--primary-foreground': buttonText,
        '--ring': link,
    };
}

function paletteProperties(palette) {
    const surface = palette.surface || palette.background;
    const muted = palette.muted || palette.text;
    const button = palette.buttonBg || palette.primary;
    const link = palette.link || palette.primary;
    const border = palette.border || muted;
    return {
        '--theme-primary': palette.primary,
        '--theme-background': palette.background,
        '--theme-text': palette.text,
        '--theme-muted': muted,
        '--theme-header': palette.headerBg || palette.background,
        '--theme-button': button,
        '--theme-button-text': palette.buttonText || '#ffffff',
        '--theme-link': link,
        '--theme-border': border,
        '--theme-surface': surface,
        '--background': palette.background,
        '--foreground': palette.text,
        '--card': surface,
        '--card-foreground': palette.text,
        '--popover': surface,
        '--popover-foreground': palette.text,
        '--primary-foreground': palette.buttonText || '#ffffff',
        '--secondary': surface,
        '--secondary-foreground': palette.text,
        '--muted': surface,
        '--muted-foreground': muted,
        '--accent': surface,
        '--accent-foreground': palette.text,
        '--border': border,
        '--input': border,
        '--ring': link,
        '--primary-color': button,
        '--primary-background-color': button,
        '--accent-text-color': link,
        '--primary-text-color': palette.text,
        '--primary-layer-color': palette.background,
        '--navbar-bg': palette.headerBg || palette.background,
        '--secondary-color': surface,
        '--secondary-background-color': surface,
        '--secondary-text-color': muted,
        '--input-black-color': palette.text,
        '--input-grey-color': muted,
        '--light-border-color': border,
    };
}

export function syncTelegramTheme(mode, palette) {
    const webApp = window.Telegram?.WebApp;
    if (!webApp?.setOptions) {
        return;
    }
    const colors = palette || standardPalette(mode);
    const background = colors.background;
    const text = colors.text;
    try {
        webApp.setOptions({
            colorScheme: mode,
            themeParams: {
                bg_color: background,
                secondary_bg_color: colors.surface || background,
                text_color: text,
                hint_color: colors.muted || text,
                link_color: colors.link || colors.primary,
                button_color: colors.buttonBg || colors.primary,
                button_text_color: colors.buttonText || '#ffffff',
            },
        });
    } catch (error) {
        // Telegram rejects theme updates outside a WebApp; the document theme still applies.
    }
    if (typeof webApp.setHeaderColor === 'function') {
        webApp.setHeaderColor(colors.headerBg || background);
    }
    if (typeof webApp.setBackgroundColor === 'function') {
        webApp.setBackgroundColor(background);
    }
}

export function applyCommunityTheme(community, mode = 'light', options = {}) {
    const root = document.documentElement;
    const skip = Boolean(options.skipSharedPalette);
    const resolved = skip
        ? { source: 'landing', palette: null }
        : resolveCommunityPalette(community, mode);
    MANAGED_PROPERTIES.forEach((property) => root.style.removeProperty(property));
    root.dataset.theme = mode;
    root.dataset.themeSource = resolved.source;
    root.style.colorScheme = mode;

    const palette = resolved.palette;
    if (palette) {
        const properties = palette.background ? paletteProperties(palette) : brandProperties(palette);
        Object.entries(properties).forEach(([property, value]) => {
            root.style.setProperty(property, value);
        });
    }

    const themeColor = palette?.background || standardPalette(mode).background;
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'theme-color');
        document.head.appendChild(meta);
    }
    meta.setAttribute('content', themeColor);
    syncTelegramTheme(mode, telegramPalette(mode, palette));
}

function telegramPalette(mode, palette) {
    if (!palette) {
        return standardPalette(mode);
    }
    if (palette.background && palette.text) {
        return palette;
    }
    return {
        ...standardPalette(mode),
        primary: palette.primary,
        headerBg: palette.headerBg,
        buttonBg: palette.buttonBg,
        buttonText: palette.buttonText,
        link: palette.link || palette.primary,
    };
}
